import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, Smartphone, Apple, Download, CheckCircle2, Shield, Zap, Globe, Moon, Sun } from 'lucide-react';
import { useTheme } from '../utils/ThemeSwitcher';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';

// Simple Tux (Linux penguin) icon
const TuxIcon = ({ size = 24, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M12 2C10.34 2 9 3.34 9 5c0 .28.04.55.11.8C7.28 6.63 6 8.68 6 11c0 1.5.5 2.87 1.33 3.97C6.5 16.03 6 17.44 6 19c0 .34.03.67.08 1H8c0-1 .25-2 .7-2.85C9.56 17.7 10.73 18 12 18s2.44-.3 3.3-.85c.45.85.7 1.85.7 2.85h1.92c.05-.33.08-.66.08-1 0-1.56-.5-2.97-1.33-4.03C17.5 13.87 18 12.5 18 11c0-2.32-1.28-4.37-3.11-5.2.07-.25.11-.52.11-.8 0-1.66-1.34-3-3-3zm-1.5 5.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm3 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM12 14c-1.1 0-2-.45-2-1h4c0 .55-.9 1-2 1z"/>
  </svg>
);


const DownloadPage = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [hoveredPlatform, setHoveredPlatform] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const c = {
    bg: isDarkMode ? '#08090B' : '#FFFFFF',
    bgAlt: isDarkMode ? '#0E1015' : '#F7F8FA',
    bgCard: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    border: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    borderHover: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)',
    text: isDarkMode ? '#7d7d81' : '#111214',
    textSecondary: isDarkMode ? '#6d6e74' : '#6B7280',
    textMuted: isDarkMode ? '#5A5C66' : '#9CA3AF',
    accent: '#2B8AFF',
    accentGold: '#D4A44C',
    accentGradient: 'linear-gradient(135deg, #105fc0 0%, #105fc0 15%, #2B8AFF 35%, #ffc55b 100%)',
    success: '#10B981',
    caramel: '#D4A574',
  };

  const platforms = [
    {
      id: 'windows',
      name: 'Windows',
      icon: Monitor,
      version: 'v1.0.1',
      size: '~554.20 MB',
      requirement: 'Windows 10 or later',
      status: 'available',
      downloadUrl: 'https://github.com/mwiingac/dataquad-releases/releases/download/v1.0.0/DataQuad_1.0.1_x64-setup.exe',
      badge: 'Available',
      storeLabel: 'Direct Download',
      description: 'Full desktop experience with native performance. Download the installer directly.',
    },
    {
      id: 'android',
      name: 'Android',
      icon: Smartphone,
      version: 'v1.0.0',
      size: '~28 MB',
      requirement: 'Android 7.0+',
      status: 'coming_soon',
      downloadUrl: null,
      badge: 'Under Review',
      storeLabel: 'Google Play',
      description: 'Currently under Google Play Store review. Available soon for download on all Android devices.',
    },
    {
      id: 'macos',
      name: 'macOS',
      icon: Apple,
      version: '—',
      size: '—',
      requirement: 'macOS 11+',
      status: 'planned',
      downloadUrl: null,
      badge: 'Planned',
      description: 'macOS support is on our roadmap. Stay tuned for updates.',
    },
    {
      id: 'linux',
      name: 'Linux',
      icon: TuxIcon,
      version: '—',
      size: '—',
      requirement: 'Ubuntu 20.04+, Fedora 36+',
      status: 'planned',
      downloadUrl: null,
      badge: 'Planned',
      description: 'Linux support is on our roadmap with .deb and .AppImage packages.',
    },
  ];

  const features = [
    { icon: Zap, label: 'Native Performance', desc: 'Built with Tauri for near-native speed' },
    { icon: Shield, label: 'Secure by Design', desc: 'Your data stays on your device and our secure servers' },
    { icon: Globe, label: 'Cross-Platform', desc: 'Same powerful features across all platforms' },
  ];

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: c.bg, color: c.text,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* ---- NAVBAR (matches landing page) ---- */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 24px',
        background: scrolled
          ? (isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)')
          : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.border}` : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src={isDarkMode ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: '28px', height: '28px' }} />
            <span style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.3px' }}>dataQuad</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={toggleTheme} style={{ background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer', padding: '6px', display: 'flex' }}>
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: c.textSecondary, fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', padding: '8px 16px' }}>Sign in</button>
            <button onClick={() => navigate('/login?signup=1')} style={{
              background: c.accentGradient, color: isDarkMode ? '#000' : '#FFF', border: 'none',
              padding: '9px 22px', borderRadius: '10px', fontSize: '14px',
              fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
            }}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* ---- CONTENT ---- */}
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '120px 24px 80px' }}>

        {/* Header */}
        <div style={{
          textAlign: 'center', marginBottom: '56px',
          opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s ease',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '6px 16px', borderRadius: '999px', marginBottom: '20px',
            backgroundColor: isDarkMode ? 'rgba(43,138,255,0.1)' : 'rgba(43,138,255,0.06)',
            border: `1px solid ${isDarkMode ? 'rgba(43,138,255,0.2)' : 'rgba(43,138,255,0.12)'}`,
            fontSize: '13px', fontWeight: 500, color: c.accent,
          }}>
            <Download size={14} />
            Desktop & Mobile Apps
          </div>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 700,
            lineHeight: 1.15, margin: '0 0 16px', color: c.text,
          }}>
            Download dataQuad
          </h1>
          <p style={{
            fontSize: '18px', color: c.textSecondary,
            maxWidth: '520px', margin: '0 auto', lineHeight: 1.6,
          }}>
            The same powerful data quality assessment you know from the web, now available as a native app.
          </p>
        </div>

        {/* Prefer web note */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '14px 20px', borderRadius: '12px', marginBottom: '32px',
          backgroundColor: isDarkMode ? 'rgba(43,138,255,0.06)' : 'rgba(43,138,255,0.04)',
          border: `1px solid ${isDarkMode ? 'rgba(43,138,255,0.15)' : 'rgba(43,138,255,0.1)'}`,
        }}>
          <Globe size={18} style={{ color: c.accent, flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: '14px', color: c.textSecondary, lineHeight: 1.5 }}>
            <span style={{ color: c.text, fontWeight: 500 }}>Prefer the web?</span>{' '}
            dataQuad is fully available at{' '}
            <a href="https://dataquadapp.com" style={{ color: c.accent, textDecoration: 'none', fontWeight: 500 }}>
              dataquadapp.com
            </a>
            {' '}— no download required.
          </p>
        </div>

        {/* Platform cards */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px',
          marginBottom: '56px',
        }}>
          {platforms.map((platform, idx) => {
            const Icon = platform.icon;
            const isAvailable = platform.status === 'available';
            const isReview = platform.status === 'coming_soon';
            const isHovered = hoveredPlatform === platform.id;

            return (
              <div
                key={platform.id}
                onMouseEnter={() => setHoveredPlatform(platform.id)}
                onMouseLeave={() => setHoveredPlatform(null)}
                style={{
                  position: 'relative',
                  padding: '28px 24px',
                  borderRadius: '16px',
                  backgroundColor: isDarkMode
                    ? (isHovered ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)')
                    : (isHovered ? '#F3F4F6' : '#FAFAFA'),
                  border: `1px solid ${isHovered ? c.borderHover : c.border}`,
                  transition: 'all 0.25s ease',
                  transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
                  opacity: mounted ? 1 : 0,
                  animation: mounted ? `fadeSlideUp 0.5s ease ${0.15 + idx * 0.08}s both` : 'none',
                  cursor: isAvailable ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (isAvailable && platform.downloadUrl) {
                    window.location.href = platform.downloadUrl;
                  }
                }}
              >
                {/* Status badge */}
                <div style={{
                  position: 'absolute', top: '16px', right: '16px',
                  padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                  letterSpacing: '0.3px', textTransform: 'uppercase',
                  backgroundColor: isAvailable
                    ? (isDarkMode ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)')
                    : isReview
                    ? (isDarkMode ? 'rgba(212,165,116,0.12)' : 'rgba(212,165,116,0.08)')
                    : (isDarkMode ? 'rgba(90,92,102,0.15)' : 'rgba(156,163,175,0.12)'),
                  color: isAvailable ? c.success : isReview ? c.caramel : c.textMuted,
                  border: `1px solid ${
                    isAvailable ? (isDarkMode ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.2)')
                    : isReview ? (isDarkMode ? 'rgba(212,165,116,0.25)' : 'rgba(212,165,116,0.2)')
                    : (isDarkMode ? 'rgba(90,92,102,0.2)' : 'rgba(156,163,175,0.15)')
                  }`,
                }}>
                  {platform.badge}
                </div>

                {/* Icon */}
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: (isAvailable || isReview)
                    ? (isDarkMode ? 'rgba(43,138,255,0.1)' : 'rgba(43,138,255,0.06)')
                    : (isDarkMode ? 'rgba(90,92,102,0.1)' : 'rgba(156,163,175,0.08)'),
                  marginBottom: '16px',
                }}>
                  <Icon size={24} style={{ color: (isAvailable || isReview) ? c.accent : c.textMuted }} />
                </div>

                <h3 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 600, color: c.text }}>{platform.name}</h3>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', color: c.textMuted }}>{platform.version}</span>
                  {platform.size !== '—' && <span style={{ fontSize: '13px', color: c.textMuted }}>· {platform.size}</span>}
                </div>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: c.textSecondary, lineHeight: 1.55 }}>{platform.description}</p>
                <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '16px' }}>Requires: {platform.requirement}</div>

                {/* Action */}
                {isAvailable ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px', borderRadius: '10px',
                    background: isHovered ? c.accentGradient : (isDarkMode ? 'rgba(43,138,255,0.12)' : 'rgba(43,138,255,0.08)'),
                    color: isHovered ? (isDarkMode ? '#000' : '#FFF') : c.accent,
                    fontSize: '14px', fontWeight: 600, transition: 'all 0.25s ease',
                  }}>
                    <Download size={16} />
                    Download for {platform.name}
                  </div>
                ) : isReview ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px', borderRadius: '10px',
                    backgroundColor: isDarkMode ? 'rgba(212,165,116,0.08)' : 'rgba(212,165,116,0.06)',
                    color: c.caramel, fontSize: '14px', fontWeight: 500,
                    border: `1px solid ${isDarkMode ? 'rgba(212,165,116,0.18)' : 'rgba(212,165,116,0.15)'}`,
                  }}>
                    <CheckCircle2 size={16} />
                    Coming to {platform.storeLabel}
                  </div>
                ) : (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '12px', borderRadius: '10px',
                    backgroundColor: isDarkMode ? 'rgba(90,92,102,0.06)' : 'rgba(156,163,175,0.06)',
                    color: c.textMuted, fontSize: '14px', fontWeight: 500,
                    border: `1px dashed ${isDarkMode ? 'rgba(90,92,102,0.25)' : 'rgba(156,163,175,0.25)'}`,
                  }}>
                    Coming Soon
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Why native */}
        <div style={{
          padding: '36px', borderRadius: '16px',
          background: c.bgAlt, border: `1px solid ${c.border}`, marginBottom: '40px',
        }}>
          <h2 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: 600, color: c.text }}>Why download the app?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            {features.map((f, i) => {
              const FIcon = f.icon;
              return (
                <div key={i} style={{ display: 'flex', gap: '14px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: isDarkMode ? 'rgba(43,138,255,0.1)' : 'rgba(43,138,255,0.06)',
                  }}>
                    <FIcon size={18} style={{ color: c.accent }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: c.text, marginBottom: '4px' }}>{f.label}</div>
                    <div style={{ fontSize: '13px', color: c.textSecondary, lineHeight: 1.45 }}>{f.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '13px', color: c.textMuted, lineHeight: 1.6 }}>
            dataQuad is a product of{' '}
            <a href="https://noblesyntax.com" style={{ color: c.textSecondary, textDecoration: 'none' }}>Noble Syntax Limited</a>. Lusaka, Zambia.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}; }
      `}</style>
    </div>
  );
};

export default DownloadPage;